<template>

    <v-product-line-layout :group="group" :collapseAllVariations="collapseAllVariations" ref="productLayout">
        <slot slot="title">{{group.ErpId}} - {{ group.Name }}</slot>
        <slot slot="product">
            <v-gallery :images="Images"></v-gallery>
            <div class="c-product-colors">
                <strong>{{ $t('product.thumb.colors') }}</strong>
                <ul class="c-product-colors__list u-margin-bottom">
                    <li class="u-font-small" v-for="color in group.Colors" :key="color">
                        <a href="#" @click="scrollToColor($event, color)">{{ color }}</a>
                    </li>
                </ul>
                <strong v-if="group.CampaignModels.length > 0">{{ $t('product.thumb.campaignmodel') }}</strong>
                <ul v-if="group.CampaignModels.length > 0" class="c-product-colors__list">
                    <li class="u-font-small u-color-info-base" v-for="model in group.CampaignModels">{{ model }}</li>
                </ul>
            </div>
            <div class="c-product-right u-font-small">
                <div>
                    <div class="c-product-info-parent">
                        <table class="c-product-info">
                            <tr>
                                <td><strong class="u-semi-bold">{{ $t('product.thumb.brand') }}:</strong></td>
                                <td>{{ group.Brand }}</td>
                            </tr>
                            <tr>
                                <td><strong class="u-semi-bold">{{ $t('product.thumb.category') }}:</strong></td>
                                <td>{{ group.BrandSubgroup }}</td>
                            </tr>
                            <tr v-if="group.Type">
                                <td><strong class="u-semi-bold">{{ $t('product.thumb.segment') }}:</strong></td>
                                <td>{{ group.Type }}</td>
                            </tr>
                        </table>

                        <table class="c-product-info">
                            <tr v-if="group.Brochure">
                                <td><strong class="u-semi-bold">{{ $t('product.thumb.brochure') }}:</strong></td>
                                <td>{{ group.Brochure }}</td>
                            </tr>
                            <tr v-if="group.Packaging">
                                <td><strong class="u-semi-bold">{{ $t('product.thumb.packaging') }}:</strong></td>
                                <td>{{ group.Packaging }}</td>
                            </tr>
                            <tr v-if="isEditableField">
                                <td><strong class="u-semi-bold">{{ $t('product.thumb.preorder_discount') }}:</strong></td>
                                <td>
                                    <v-input v-model.number="group.PreOrderDiscount" :name="`${_uid}-discount`" type="number" width="50px" :min=0
                                             :max="MaximumDiscount" input-size="tiny" :disabled="isDisabled"
                                             @change="updateGroup"
                                             v-tooltip.left-end="'Aanpassingen aan de algemene korting zal alle kortingen bij producten overschrijven.'">
                                        <slot slot="suffix">({{ $t('product.thumb.max') }} {{ group.MaximumDiscount }}%)</slot>
                                    </v-input>
                                </td>
                            </tr>
                        </table>
                    </div>
                </div>
                <div class="c-product-info-parent" v-if="group.Note">
                        <table class="c-product-info">
                            <tr>
                                <td><strong class="u-semi-bold">Opmerking:</strong></td>
                                <td>{{ group.Note }}</td>
                            </tr>
                        </table>
                    </div>
                <div class="c-product-summary">
                    <span>
                        <strong class="u-text-uppercase u-semi-bold">{{ $t('product.thumb.total') }}</strong> <br>
                        <span class="u-font-default">{{ TotalAmount }}</span>
                    </span>
                    <span>
                        <strong class="u-text-uppercase u-semi-bold">{{ $t('product.thumb.total_price') }}</strong> <br>
                        <span class="u-font-default">{{ TotalPrice | currency(customerCurrencySymbol) }}</span>
                    </span>
                    <span>
                        <strong class="u-text-uppercase u-semi-bold">{{ $t('product.thumb.total_netto_price') }}</strong> <br>
                        <span class="u-font-default">{{ NettoPrice | currency(customerCurrencySymbol) }}</span>
                    </span>
                </div>
            </div>
        </slot>
        <slot slot="variations">
            <v-product-variation-line :parent-id="parentId"
                                      v-for="product in group.Products"
                                      :product="product"
                                      :key="product.Id"
                                      :group-index="groupIndex"
                                      :is-editable-field="isEditableField"
                                      :readOnly="readOnly"
                                      :sizeChartCopy="sizeChartCopy"
                                      @sizeChartCopied="sizeChartCopied">

            </v-product-variation-line>
        </slot>
    </v-product-line-layout>
</template>

<script>
    import debounce from 'lodash/debounce'
    import ScrollTo from 'vue-scrollto'
    import VGallery from '@/components/gallery/Gallery'
    import VSelect from '@/components/form/Select'
    import VInput from '@/components/form/Input'
    import VProductVariationLine from '@/components/product/common/ProductVariationLine'
    import VProductLineLayout from '@/components/product/layout/ProductLineLayout'

    export default {
        components: {
            VGallery,
            VSelect,
            VInput,
            VProductVariationLine,
            VProductLineLayout
        },
        props: {
            group: Object,
            parentId: String,
            groupIndex: Number,
            isEditableField: { type: Boolean, default: true },
            collapseAllVariations: Boolean,
            readOnly: {type: Boolean, default: false},
            sizeChartCopy: Object
        },
        computed: {
            /**
             * @return {number}
             */
            TotalAmount() {
              let amountArray = this.group.Products.map(result => result.TotalAmount);
              return amountArray.reduce((a, b) => a + b);
            },
            /**
             * @return {number}
             */
            TotalPrice() {
              let totalPriceArray = this.group.Products.map(result => result.TotalPrice);
              return totalPriceArray.reduce((a, b) => a + b);
            },
            /**
             * @return {number}
             */
            NettoPrice() {
              let nettoPriceArray = this.group.Products.map(result => result.NettoPrice);
              return nettoPriceArray.reduce((a, b) => a + b);
            },
            Images() {
                let output = !this.group.Images.length ? null : [];

                this.group.Images.forEach((image, index) => {
                    if (index < 6) {
                        output.push({
                            src: image.replace('[WIDTH]', 230).replace('[HEIGHT]', 180),
                            selected: index === 0
                        })
                    }
                });
                return output
            },
            isDisabled(){
                return this.group.IsDeleted || this.readOnly;
            },
            MaximumDiscount() {
                let discount;
                if (this.group.MaximumDiscount) {
                    discount = this.group.MaximumDiscount;
                } else {
                    discount = 0;
                }
                return discount;
            },
            customerCurrencySymbol() { 
                return this.$store.getters.userInfo.customerCurrencySymbol;
            }
        },
        methods: {
            updateGroup: debounce(function () {
                if (this.group.PreOrderDiscount <= this.group.MaximumDiscount) {
                    this.$store.dispatch('updatePreOrderGroup', {
                        preOrderId: this.parentId,
                        productGroupId: this.group.Id,
                        body: {
                            Id: this.group.Id,
                            PreOrderDiscount: this.group.PreOrderDiscount
                        }
                    }).then(() => {
                        this.group.Products.forEach(product => product.PreOrderDiscount = this.group.PreOrderDiscount);
                    });
                }
            }, 400),
            sizeChartCopied(sizeChartCopy) {
                this.$emit('sizeChartCopied', sizeChartCopy)
            },
            getScrollToAnchor(color){
                let product = this.group.Products.find(product => {
                    return (product.ColorId + ' ' + product.Color) === color;
                });

                if(product) {
                    return '#variant_' + product.Id;
                }
            },
            scrollToColor(e, color){
                e.preventDefault();
                this.$refs.productLayout.collapseAllVariations = false;
                const anchor = this.getScrollToAnchor(color);

                setTimeout(function() {
                    ScrollTo.scrollTo(anchor, 300, {offset: -55});
                }, 1);
            }
        }
    }
</script>

<style lang="scss" scoped>

    @import "~@/assets/scss/private";


</style>



// WEBPACK FOOTER //
// src/components/product/common/ProductLine.vue